<template>
    <form
        class="flex flex-col gap-y-4"
        @submit="handleSubmitCheckoutForm"
    >
        <customer-information
            v-model:editing="editingCustomerInformation"
            v-model:do-not-mail-visible="doNotMailVisible"
            :customer="primaryCustomer"
            :facility="facility"
            :collect-do-not-mail="collectDoNotMail"
            :always-show-do-not-mail="alwaysShowDoNotMail"
            :show-errors="submitCount > 0"
        />

        <checkout-section-separator />

        <billing-address
            v-model:editing="editingBillingAddress"
            :customer="primaryCustomer"
            :is-full-address="fullAddressRequired"
            :title="t('checkout.billingAddress')"
        />

        <checkout-section-separator
            v-if="requiresRecurringToken && readyForRecurringToken"
        />

        <recurring-information
            v-if="requiresRecurringToken && readyForRecurringToken"
            ref="recurringInformationRef"
            v-model:editing="editingRecurringInformation"
            :customer="primaryCustomer"
            :facility="facility"
            :recurring-total="recurringTotal"
            :payment-method-input="paymentMethodInput"
        />

        <checkout-section-separator v-if="readyForPayment" />

        <payment-information
            v-if="readyForPayment"
            ref="paymentInformationRef"
            :customer="primaryCustomer"
            :facility="facility"
            :checkout-total="dueToday"
            :title="paymentTitle"
            :payment-method-input="paymentMethodInput"
            @windcave-purchase-session-created="
                handleWindcavePurchaseSessionCreated
            "
        />

        <div
            v-if="
                readyForCheckoutSubmit &&
                requiresRecaptcha &&
                armedWindcaveSessionId === null
            "
            class="mb-3"
        >
            <rp-recaptcha
                ref="recaptchaRef"
                @token="handleRecaptchaToken"
            />
            <span class="text-small text-red-600">
                {{
                    submitCount > 0
                        ? errors.recaptchaToken || '&nbsp;'
                        : '&nbsp;'
                }}
            </span>
        </div>

        <div
            v-if="showPleaseFixMessage"
            class="text-red-600 font-medium"
        >
            {{ t('checkout.pleaseFixErrorsInRedAbove') }}
        </div>

        <div>
            <prime-button
                v-if="readyForPayment"
                id="place_order"
                severity="success"
                :label="t('purchaseContract.documentSection.payNow')"
                type="submit"
                class="w-full"
                :loading="isPending || isValidating || isProcessing"
            />

            <div v-else>
                <prime-button
                    v-if="dueToday.isZero()"
                    id="place_order"
                    severity="success"
                    :label="t('checkout.completePurchase')"
                    type="submit"
                    class="w-full"
                    :loading="isValidating || isProcessing"
                />

                <prime-button
                    v-else
                    :label="t('checkout.continueToPayment')"
                    type="submit"
                    class="w-full"
                    :loading="isValidating || isProcessing"
                />
            </div>
        </div>
    </form>
</template>

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/yup'
import { object, string } from 'yup'
import { useForm } from 'vee-validate'

import { CheckoutSuccessFragment } from '~/components/Checkout/CheckoutSuccess.vue'
import PrimeButton from 'primevue/button'
import { useRpQuery, UserReportableError } from '~/composables/graphql'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { useSelectedLanguage } from '~/composables/use-selected-language'
import type {
    CommitCheckoutCustomerMutation,
    CheckoutRecurringInput,
    CommitCheckoutInput,
    ValidateCheckoutCustomerMutation,
    ValidateCheckoutInput
} from '~/resources/graphql/graphql'
import { computed, nextTick, onMounted, ref, toRef, watch } from 'vue'
import { type FragmentType, graphql } from '~/resources/graphql'
import { GraphQLRequestError } from 'awesome-graphql-client'
import { useActiveFacility } from '~/composables/use-active-facility'

import {
    CheckoutFormCheckoutFragment,
    CheckoutFormQueryFragment,
    useCheckoutForm
} from '~/components/Checkout/Forms/use-checkout-form'

import CustomerInformation, {
    CustomerInformationCheckoutFormInitialValues,
    CustomerInformationCheckoutFormSchema,
    type CustomerInformationSchemaType
} from '~/components/Checkout/Sections/CustomerInformation.vue'
import BillingAddress, {
    BillingAddressInitialValues,
    BillingAddressSchema,
    type BillingAddressSchemaTyped
} from '~/components/Checkout/Sections/BillingAddress.vue'
import RecurringInformation, {
    RecurringInformationInitialValues,
    RecurringInformationSchema,
    type RecurringInformationSchemaType
} from '~/components/Checkout/Sections/RecurringInformation.vue'
import RpRecaptcha from '~/components/Verification/RpRecaptcha.vue'
import PaymentInformation, {
    PaymentInformationInitialValues,
    PaymentInformationSchema,
    type PaymentInformationSchemaType
} from '~/components/Checkout/Sections/PaymentInformation.vue'
import { useShowAlert } from '~/composables/use-show-alert'
import CheckoutSectionSeparator from '~/components/Checkout/Forms/CheckoutSectionSeparator.vue'
import {
    checkoutPaymentInput,
    checkoutRecurringInput,
    resetPaymentErrors
} from '~/components/Checkout/Forms/checkout-helpers'
import { usePaymentMethodInput } from '~/components/Checkout/Sections/use-payment-method-input'
import { useI18n } from 'vue-i18n'
import { ContractReadyForPurchase } from '../../../../resources/app/generated/enums'
import {
    type CheckoutPhoneVerificationModalResult,
    useCheckoutPhoneVerificationModal
} from '~/composables/use-checkout-phone-verification'
import { PaymentSubmissionResult } from '~/components/Checkout/Sections/Payment/payment-method'
import { doNotMailStatusFromValue } from '~/components/PurchaseContract/do-not-mail-helpers'

const props = defineProps<{
    query: FragmentType<typeof CheckoutFormQueryFragment>
    checkout: FragmentType<typeof CheckoutFormCheckoutFragment>
}>()

const emit = defineEmits<{
    (
        e: 'checkoutSuccess',
        payload: FragmentType<typeof CheckoutSuccessFragment>
    ): void
    (e: 'checkoutStatusChanged'): void
}>()

type CheckoutFormValues = {
    customer: CustomerInformationSchemaType
    address: BillingAddressSchemaTyped
    recurring: RecurringInformationSchemaType
    payment: PaymentInformationSchemaType
    recaptchaToken: string
}

type CommitCheckoutMutationData = NonNullable<
    CommitCheckoutCustomerMutation['commitCheckout']
>
type ValidateCheckoutMutationData = NonNullable<
    ValidateCheckoutCustomerMutation['validateCheckout']
>

const { t } = useI18n({ useScope: 'global' })

// A successful preflight consumes reCAPTCHA and binds it to this server-owned
// session. The final commit replays that intent and must not depend on the
// short-lived browser token remaining valid.
const armedWindcaveSessionId = ref<string | null>(null)

const {
    checkoutId,

    primaryCustomer,
    facility,

    fullAddressRequired,
    requiresCustomerInformation,
    collectDoNotMail,
    alwaysShowDoNotMail,
    requiresRecaptcha,

    editingCustomerInformation,
    doNotMailVisible,
    editingBillingAddress,
    editingRecurringInformation,

    dueToday,
    recurringTotal,
    requiresRecurringToken,

    isProcessing,
    incrementProcessing,
    decrementProcessing
} = useCheckoutForm(toRef(props, 'query'), toRef(props, 'checkout'))

const schema = toTypedSchema(
    object({
        customer: CustomerInformationCheckoutFormSchema(t),
        address: BillingAddressSchema(t),
        recurring: RecurringInformationSchema(t),
        payment: PaymentInformationSchema(t),
        recaptchaToken: string().test(
            'required-until-windcave-preflight',
            t('captchaRequired'),
            value =>
                !requiresRecaptcha.value ||
                armedWindcaveSessionId.value !== null ||
                (typeof value === 'string' && value.length > 0)
        )
    })
)

const activeFacility = useActiveFacility()
const {
    handleSubmit,
    errors,
    validateField,
    validate,
    setErrors,
    setFieldValue,
    values
} = useForm({
    validationSchema: schema,
    initialValues: {
        customer: CustomerInformationCheckoutFormInitialValues(
            primaryCustomer.value,
            facility.value,
            activeFacility.value.organization.onlineGuestId
        ),
        address: BillingAddressInitialValues(
            primaryCustomer.value,
            fullAddressRequired.value
        ),
        recurring: RecurringInformationInitialValues(
            requiresRecurringToken.value ? recurringTotal.value : null
        ),
        payment: PaymentInformationInitialValues(dueToday.value),
        recaptchaToken: ''
    }
})
// Snapshot initial visibility so clearing an existing middle name still submits the empty value.
// Making this reactive to the current value would omit the field as soon as the customer clears it.
const submitMiddleName =
    activeFacility.value.organization.useMiddleName ||
    values.customer.middleName !== ''

const showPleaseFixMessage = computed(() => {
    // No submission yet means don't show the error message
    if (submitCount.value < 1) {
        return false
    }

    // If we are on the pay now portion, show all errors
    if (readyForPayment.value) {
        return Object.keys(errors.value).length > 0
    }

    // Otherwise show errors with payment filtered out
    return (
        Object.keys(errors.value).filter(
            field => !field.startsWith('payment') && field !== 'recaptchaToken'
        ).length > 0
    )
})

const pendingCustomerInformation = ref(requiresCustomerInformation.value)
const readyForRecurringToken = computed(
    () => pendingCustomerInformation.value === false
)
watch(editingCustomerInformation, (newValue, oldValue) => {
    // If we require customer information for the payment methods, when the
    // customer information changes we have to make a new session, so reset the form
    if (requiresCustomerInformation.value) {
        if (oldValue === false && newValue === true) {
            pendingCustomerInformation.value = true
        }
    }
})
watch(editingBillingAddress, (newValue, oldValue) => {
    // If we require customer information for the payment methods, when the
    // customer information changes we have to make a new session, so reset the form
    if (requiresCustomerInformation.value) {
        if (oldValue === false && newValue === true) {
            pendingCustomerInformation.value = true
        }
    }
})

const { customer, address, paymentMethodInput } = usePaymentMethodInput()
onMounted(() => {
    if (requiresCustomerInformation.value) {
        // Only skip confirmation when neither child section has information left to collect.
        if (
            customer.value.email !== '' &&
            address.value.postal !== '' &&
            !editingCustomerInformation.value &&
            !editingBillingAddress.value
        ) {
            pendingCustomerInformation.value = false
        }
    }
})

const pendingRecurringToken = ref(requiresRecurringToken.value)
const readyForPayment = computed(
    () =>
        pendingCustomerInformation.value === false &&
        pendingRecurringToken.value === false
)
const readyForCheckoutSubmit = computed(() => {
    // The backend requires reCAPTCHA for every online commitCheckout request.
    // Zero-dollar recurring checkouts submit from the recurring step without
    // showing payment fields, so CAPTCHA must be available there too.
    return (
        readyForPayment.value ||
        (pendingCustomerInformation.value === false &&
            pendingRecurringToken.value &&
            dueToday.value.isZero())
    )
})

const isValidating = ref(false)
// We use our own submit count because the continue to payment button does not submit the vee validate form
const submitCount = ref(0)
const paymentTitle = computed(() => {
    if (requiresRecurringToken.value) {
        return t('checkout.paymentDueToday')
    } else {
        return t('payment')
    }
})

// When editing the recurring information section, reset the form value
watch(editingRecurringInformation, (newValue, oldValue) => {
    if (oldValue === false && newValue === true) {
        pendingRecurringToken.value = true
    }
})

// References to components allowing us to submit them
const recurringInformationRef = ref<InstanceType<typeof RecurringInformation>>()
const paymentInformationRef = ref<InstanceType<typeof PaymentInformation>>()
const recaptchaRef = ref<InstanceType<typeof RpRecaptcha>>()
const preservedWindcaveSessionPaymentMethod =
    ref<PaymentInformationSchemaType['paymentMethod']>(null)

function handleRecaptchaToken(token: string) {
    // Google also emits an empty token when checkout resets the widget after preflight.
    // Keep form state synchronized without revalidating the whole form unless the
    // challenge is still a visible input that the customer must complete.
    const recaptchaIsInUse =
        readyForCheckoutSubmit.value &&
        requiresRecaptcha.value &&
        armedWindcaveSessionId.value === null
    setFieldValue('recaptchaToken', token, recaptchaIsInUse)
}

function handleWindcavePurchaseSessionCreated(sessionId: string) {
    if (
        armedWindcaveSessionId.value !== null &&
        armedWindcaveSessionId.value !== sessionId
    ) {
        // A replacement hosted form represents a new payment attempt, so it
        // needs a new one-time human proof before it can be armed.
        armedWindcaveSessionId.value = null
    }
}

// Main submit function
async function handleSubmitCheckoutForm(event: SubmitEvent) {
    // Prevent form action
    event.preventDefault()
    submitCount.value += 1
    preflightPhoneVerificationInput.value = undefined

    // Lock every checkout control before validation because validation and payment
    // preflight yield long enough for edits to invalidate the active payment section.
    incrementProcessing()
    isValidating.value = true
    try {
        // Just get the customer information since the payment processor wants
        // this up front
        if (pendingCustomerInformation.value) {
            // Otherwise, validate the entire form
            const result = await validate()
            const isValid = Object.entries(result.results).every(
                ([key, value]) => {
                    return (
                        key.startsWith('payment') ||
                        key.startsWith('recurring') ||
                        key === 'recaptchaToken' ||
                        value.valid
                    )
                }
            )

            resetPaymentErrors(setErrors)

            // Ready for recurring token or payment!
            if (isValid) {
                submitCount.value = 0 // reset submit count
                editingCustomerInformation.value = false
                editingBillingAddress.value = false
                pendingCustomerInformation.value = false

                // Recurring payment method required?
                if (requiresRecurringToken.value) {
                    pendingRecurringToken.value = true
                    editingRecurringInformation.value = true
                }
            } else {
                // TODO: Find a way to scroll up
            }

            return
        }

        // Are we collecting recurring information?
        // Recurring checkout
        if (pendingRecurringToken.value) {
            // If recurring field is not valid, submit stripe form
            // We use silent so we don't show the error message until after stripe form is submitted
            const { valid: validRecurring } = await validateField('recurring', {
                mode: 'silent'
            })
            if (!validRecurring) {
                await recurringInformationRef.value.submitRecurringForm()
            }

            // Otherwise, validate the entire form
            const result = await validate()
            const isValid = Object.entries(result.results).every(
                ([key, value]) => {
                    return (
                        key.startsWith('payment') ||
                        key === 'recaptchaToken' ||
                        value.valid
                    )
                }
            )

            // Ready for payment!
            if (isValid) {
                // Zero-dollar transaction, so submit the form
                if (dueToday.value.isZero()) {
                    // Await here so a rejected submit promise reaches this function's reporting catch.
                    await onSubmit(event)
                    return
                }
                // Close all the other sections
                else {
                    submitCount.value = 0 // reset submit count
                    editingCustomerInformation.value = false
                    editingBillingAddress.value = false
                    pendingRecurringToken.value = false
                    editingRecurringInformation.value = false
                }
            } else {
                // TODO: Find a way to scroll up
            }

            return
        }

        // Otherwise, validate the entire form
        const result = await validate()

        // Validation yields to Vue, so editing an earlier section can unmount payment mid-submit.
        // Stop this attempt and let the customer finish that section instead of using stale form state.
        if (!readyForPayment.value || values.payment === undefined) {
            return
        }

        const isValid = Object.entries(result.results).every(([key, value]) => {
            return key.startsWith('payment') || value.valid
        })

        if (isValid) {
            // CAPTCHA expiry only invalidates the human-check field; when the
            // selected payment still has the completed WC session, never send it again.
            const selectedPaymentMethod = values.payment.paymentMethod
            const hasPreservedWindcaveSession =
                preservedWindcaveSessionPaymentMethod.value !== null &&
                selectedPaymentMethod?.type === 'PaymentMethod' &&
                selectedPaymentMethod.token ===
                    preservedWindcaveSessionPaymentMethod.value.token

            if (
                !result.results.payment?.valid &&
                !hasPreservedWindcaveSession
            ) {
                // Also clear this error so we don't flash red on the payment error
                // onsubmit will validate the whole form again
                resetPaymentErrors(setErrors)

                const submittingWindcavePayment =
                    paymentInformationRef.value.shouldPreflightBeforeSubmittingPaymentForm()
                // The hosted form validates card fields before invoking this guard, then submits immediately.
                const paymentSubmissionResult =
                    await paymentInformationRef.value.submitPaymentForm(
                        submittingWindcavePayment
                            ? () =>
                                  validateCheckoutBeforePaymentCollection(
                                      values as CheckoutFormValues
                                  )
                            : undefined
                    )

                if (submittingWindcavePayment) {
                    if (
                        paymentSubmissionResult ===
                        PaymentSubmissionResult.CHECKOUT_STATUS_UNCERTAIN
                    ) {
                        // Reload the durable ARMED checkout so provider reconciliation replaces this stale form.
                        emit('checkoutStatusChanged')
                        return
                    }

                    if (
                        paymentSubmissionResult !==
                        PaymentSubmissionResult.SUBMITTED
                    ) {
                        // The preflight may have redirected the page to an existing durable checkout state.
                        return
                    }

                    // PaymentInformation applies the emitted WC session token to
                    // vee-validate state; wait for that propagation before preserving it.
                    await nextTick()
                    const submittedPaymentMethod = values.payment.paymentMethod
                    if (
                        submittedPaymentMethod?.type === 'PaymentMethod' &&
                        submittedPaymentMethod.token?.startsWith('WC|') === true
                    ) {
                        preservedWindcaveSessionPaymentMethod.value = {
                            ...submittedPaymentMethod
                        }
                    }
                }
            }

            // Await here so a rejected submit promise reaches this function's reporting catch.
            await onSubmit(event)
            return
        } else {
            // TODO: Find a way to scroll up
        }
    } catch (error) {
        const checkoutError =
            error instanceof Error ? error : new Error(String(error))

        try {
            // Send unexpected browser failures to Laravel before preserving the original rejection.
            await fetch('/error-report', {
                method: 'POST',
                headers: {
                    'content-type': 'application/json'
                },
                body: JSON.stringify({
                    key: 'checkoutSubmit',
                    message: checkoutError.message,
                    stack: checkoutError.stack ?? null
                })
            })
        } catch {
            // A diagnostic request failure must not hide or replace the checkout failure.
        }

        throw error
    } finally {
        // The successful submit callback owns a second lock until the mutation settles,
        // while validation failures and early returns release the only lock here.
        decrementProcessing()
        isValidating.value = false
    }
}

const rpQuery = useRpQuery()
const selectedLanguage = useSelectedLanguage()
const { mutate, isPending } = useMutation({
    mutationFn: async (mutationData: CommitCheckoutInput) => {
        const response = await rpQuery(
            graphql(/** GraphQL */ `
                mutation CommitCheckoutCustomer(
                    $input: CommitCheckoutInput!
                    $language: Language!
                ) {
                    commitCheckout(input: $input) {
                        __typename
                        ... on CommitCheckoutResult {
                            ...CheckoutSuccessFragment
                        }
                        ... on CommitCheckoutIncomplete {
                            count
                            results
                        }
                        ... on CheckoutPhoneVerificationRequired {
                            message
                        }
                        ... on CheckoutProcessing {
                            checkoutId
                        }
                        ... on ValidationError {
                            fieldErrors {
                                path
                                message
                            }
                        }
                    }
                }
            `),
            {
                input: mutationData,
                language: selectedLanguage.value
            }
        )

        if (response.commitCheckout === null) {
            throw new Error(t('checkout.inlineCheckoutError', { code: 'CFOC' }))
        }

        if (
            response.commitCheckout.__typename === 'CommitCheckoutResult' ||
            response.commitCheckout.__typename === 'CommitCheckoutIncomplete' ||
            response.commitCheckout.__typename ===
                'CheckoutPhoneVerificationRequired' ||
            response.commitCheckout.__typename === 'CheckoutProcessing' ||
            response.commitCheckout.__typename === 'ValidationError'
        ) {
            return response.commitCheckout
        }

        throw new Error()
    }
})

const { mutateAsync: validateCheckoutMutateAsync } = useMutation({
    mutationFn: async (mutationData: ValidateCheckoutInput) => {
        const response = await rpQuery(
            graphql(/** GraphQL */ `
                mutation ValidateCheckoutCustomer(
                    $input: ValidateCheckoutInput!
                ) {
                    validateCheckout(input: $input) {
                        __typename
                        ... on ValidateCheckoutResult {
                            value
                        }
                        ... on Checkout {
                            status
                        }
                        ... on CommitCheckoutIncomplete {
                            count
                            results
                        }
                        ... on CheckoutPhoneVerificationRequired {
                            message
                        }
                        ... on ValidationError {
                            fieldErrors {
                                path
                                message
                            }
                        }
                    }
                }
            `),
            {
                input: mutationData
            }
        )

        if (response.validateCheckout === null) {
            throw new Error(
                t('checkout.inlineCheckoutError', { code: 'VCFOC' })
            )
        }

        if (
            response.validateCheckout.__typename === 'ValidateCheckoutResult' ||
            response.validateCheckout.__typename === 'Checkout' ||
            response.validateCheckout.__typename ===
                'CommitCheckoutIncomplete' ||
            response.validateCheckout.__typename ===
                'CheckoutPhoneVerificationRequired' ||
            response.validateCheckout.__typename === 'ValidationError'
        ) {
            return response.validateCheckout
        }

        throw new Error()
    }
})

// Once form is validated, this preps and calls the mutation
const showAlert = useShowAlert()
const queryClient = useQueryClient()
const showCheckoutPhoneVerificationModal = useCheckoutPhoneVerificationModal()
const preflightPhoneVerificationInput = ref<
    CommitCheckoutInput['phoneVerification'] | undefined
>(undefined)

function handleCommitCheckoutValidationError(
    data: Extract<CommitCheckoutMutationData, { __typename: 'ValidationError' }>
) {
    // Warn, even for invalid initials here
    let messageBuilder = ''
    data.fieldErrors.forEach(error => {
        messageBuilder += error.message
    })
    if (messageBuilder !== '') {
        showAlert(t('headsUp'), messageBuilder)
    }
}

function handleCommitCheckoutIncomplete(
    data: Extract<
        CommitCheckoutMutationData,
        { __typename: 'CommitCheckoutIncomplete' }
    >
) {
    // Invalidate the checkout query to refresh any cart state that made the
    // backend reject the checkout before payment collection.
    queryClient.invalidateQueries({
        predicate: query => query.queryKey.includes('CheckoutViewerQuery')
    })

    showAlert(
        t('headsUp'),
        data.results.includes(ContractReadyForPurchase.COURSE_CANCELLED)
            ? t('cartSidebar.cancelledCourseCheckout')
            : t(
                  'cartSidebar.incompleteContracts',
                  { count: data.count },
                  data.count
              )
    )

    if (import.meta.env.DEV) {
        console.log(data.results)
    }
}

function handleCommitCheckoutMutationError(error: unknown) {
    if (
        !(error instanceof UserReportableError) &&
        !(error instanceof GraphQLRequestError)
    ) {
        showAlert(t('headsUp'), String(error))
    }
}

async function reportCommitCheckoutMutationFailure(
    mutationData: CommitCheckoutInput,
    message: string,
    context: {
        stage: string
        resultType?: string
        failureCount?: number
    },
    error?: unknown
): Promise<void> {
    try {
        const windcavePayment = mutationData.payment.items.find(paymentItem => {
            return paymentItem.token.startsWith('WC|')
        })
        const errorStack =
            error instanceof Error
                ? (error.stack ?? error.message)
                : error === undefined
                  ? null
                  : String(error)

        // Mutation callbacks run after the submit handler has returned, so report
        // failures here with the payment session needed to correlate orphaned payments.
        await fetch('/error-report', {
            method: 'POST',
            headers: {
                'content-type': 'application/json'
            },
            body: JSON.stringify({
                key:
                    windcavePayment === undefined
                        ? 'checkoutSubmit'
                        : 'checkoutWindcave',
                message,
                stack: errorStack,
                context: {
                    checkoutId: mutationData.checkoutId,
                    paymentSourceId: windcavePayment?.paymentSourceId,
                    sessionId: windcavePayment?.token.slice(3),
                    occurredAt: new Date().toISOString(),
                    ...context
                }
            })
        })
    } catch {
        // Diagnostics must never replace or interfere with checkout behavior.
    }
}

async function validateCheckoutBeforePaymentCollection(
    values: CheckoutFormValues,
    phoneVerificationAttempt?: CheckoutPhoneVerificationModalResult
): Promise<boolean> {
    const windcavePurchaseSession =
        paymentInformationRef.value?.getWindcavePurchaseSession()
    if (
        windcavePurchaseSession === null ||
        windcavePurchaseSession === undefined
    ) {
        showAlert(t('headsUp'), t('checkoutSections.unableToLoadPaymentForm'))
        return false
    }

    // The hosted form has a session before it has a final payment token. Supply that
    // exact intended item so the server can lock the cart before authorization begins.
    const payment = checkoutPaymentInput(values.payment)
    payment.items.push({
        paymentSourceId: windcavePurchaseSession.paymentSourceId,
        amount: windcavePurchaseSession.amount,
        token: 'WC|' + windcavePurchaseSession.sessionId
    })

    let recurring: CheckoutRecurringInput | null = null
    if (requiresRecurringToken.value) {
        recurring = checkoutRecurringInput(values.recurring)
    }

    const mutationData: ValidateCheckoutInput = {
        checkoutId: checkoutId.value,
        customer: buildCheckoutCustomerInput(values),
        recurring,
        payment,
        recaptchaToken: values.recaptchaToken,
        windcaveSessionId: windcavePurchaseSession.sessionId
    }

    if (preflightPhoneVerificationInput.value !== undefined) {
        mutationData.phoneVerification = preflightPhoneVerificationInput.value
    }

    let data: ValidateCheckoutMutationData
    try {
        data = await validateCheckoutMutateAsync(mutationData)
    } catch (error) {
        handleCommitCheckoutMutationError(error)
        return false
    }

    if (data.__typename === 'ValidateCheckoutResult') {
        // Preflight has durably bound the checkout to this hosted session. Drop
        // the short-lived widget and token before card authorization starts.
        armedWindcaveSessionId.value = windcavePurchaseSession.sessionId
        recaptchaRef.value?.reset()
        return true
    }

    if (data.__typename === 'Checkout') {
        // A previous request already moved checkout forward; let the page render that durable state.
        emit('checkoutStatusChanged')
        return false
    }

    if (data.__typename === 'CheckoutPhoneVerificationRequired') {
        const phoneVerification = await showCheckoutPhoneVerificationModal({
            initialMessage: data.message,
            previousVerification:
                mutationData.phoneVerification === undefined
                    ? undefined
                    : phoneVerificationAttempt
        })

        if (phoneVerification === undefined) {
            return false
        }

        // Keep the verified OTP response for both the next preflight attempt
        // and the final commit after Windcave has returned a token.
        preflightPhoneVerificationInput.value = {
            token: phoneVerification.token,
            code: phoneVerification.code
        }

        return validateCheckoutBeforePaymentCollection(
            values,
            phoneVerification
        )
    }

    if (data.__typename === 'ValidationError') {
        handleCommitCheckoutValidationError(data)
    } else if (data.__typename === 'CommitCheckoutIncomplete') {
        handleCommitCheckoutIncomplete(data)
    }

    return false
}

function submitCommitCheckoutMutation(
    mutationData: CommitCheckoutInput,
    phoneVerificationAttempt?: CheckoutPhoneVerificationModalResult
) {
    mutate(mutationData, {
        onSuccess(data) {
            if (data.__typename === 'ValidationError') {
                void reportCommitCheckoutMutationFailure(
                    mutationData,
                    'Checkout commit returned validation errors after payment submission.',
                    {
                        stage: 'commit_validation_error',
                        resultType: data.__typename,
                        failureCount: data.fieldErrors.length
                    }
                )
                handleCommitCheckoutValidationError(data)
            } else if (data.__typename === 'CommitCheckoutIncomplete') {
                void reportCommitCheckoutMutationFailure(
                    mutationData,
                    'Checkout commit returned incomplete contracts after payment submission.',
                    {
                        stage: 'commit_checkout_incomplete',
                        resultType: data.__typename,
                        failureCount: data.count
                    }
                )
                handleCommitCheckoutIncomplete(data)
            } else if (
                data.__typename === 'CheckoutPhoneVerificationRequired'
            ) {
                showCheckoutPhoneVerificationModal({
                    initialMessage: data.message,
                    previousVerification:
                        mutationData.phoneVerification === undefined
                            ? undefined
                            : phoneVerificationAttempt
                }).then(phoneVerification => {
                    if (phoneVerification !== undefined) {
                        const checkoutPhoneVerification = {
                            token: phoneVerification.token,
                            code: phoneVerification.code
                        }

                        incrementProcessing()
                        submitCommitCheckoutMutation(
                            {
                                ...mutationData,
                                phoneVerification: checkoutPhoneVerification
                            },
                            phoneVerification
                        )
                    }
                })
            } else if (data.__typename === 'CheckoutProcessing') {
                // Refetching lets the checkout page replace the editable form with bounded processing polling.
                emit('checkoutStatusChanged')
            } else if (data.__typename === 'CommitCheckoutResult') {
                emit('checkoutSuccess', data)
            }
        },
        onError(error) {
            void reportCommitCheckoutMutationFailure(
                mutationData,
                'Checkout commit mutation failed after payment submission.',
                { stage: 'commit_mutation_error' },
                error
            )
            handleCommitCheckoutMutationError(error)

            // A confirmed decline can cross the daily threshold, so refetch before allowing another attempt.
            const refreshCheckout = queryClient.invalidateQueries({
                predicate: query =>
                    query.queryKey.includes('CheckoutViewerQuery')
            })
            if (
                mutationData.payment.items.some(paymentItem =>
                    paymentItem.token.startsWith('WC|')
                )
            ) {
                // Refetch first so processing/error checkouts unmount before a retry can request a session.
                void refreshCheckout.then(() => {
                    // Only confirmed declines still render this form and need a fresh hosted session.
                    paymentInformationRef.value?.retryWindcavePayment()
                })
            } else {
                // Non-Windcave retries use the refreshed query only to mount the newly-required challenge.
                void refreshCheckout
            }
        },
        onSettled(data) {
            if (data?.__typename !== 'CheckoutPhoneVerificationRequired') {
                // Phone verification returns before the backend consumes the
                // CAPTCHA token, so keep the widget response for the verified retry.
                recaptchaRef.value?.reset()
                // The reset event clears form state through handleRecaptchaToken.
                submitCount.value = 0
            }
            decrementProcessing()
        }
    })
}

function buildCheckoutCustomerInput(
    values: CheckoutFormValues
): CommitCheckoutInput['customer'] {
    // Customer and Address
    const middleName = submitMiddleName ? values.customer.middleName : undefined

    const doNotMailStatus = doNotMailVisible.value
        ? doNotMailStatusFromValue(values.customer.doNotMail)
        : undefined
    const customer = {
        ...values.customer,
        // Preserve existing customer data by omitting a hidden optional field rather than sending a blank value.
        middleName,
        // The status field supersedes the legacy Boolean input.
        doNotMail: undefined,
        // Hidden consent was not reviewed during this checkout and must not update the customer record.
        doNotMailStatus,
        dateOfBirth: null as string | null,
        mobilePhone: values.customer.mobilePhone.number,
        address: {
            ...values.address,
            // Wipe out the full address
            fullAddress: undefined
        }
    }

    // Add date of birth if it exists
    if (values.customer.dateOfBirth !== '') {
        customer.dateOfBirth = values.customer.dateOfBirth
    }

    return customer
}

const onSubmit = handleSubmit(async values => {
    incrementProcessing()

    const checkoutValues = values as CheckoutFormValues

    // Recurring Token
    let recurring: CheckoutRecurringInput | null = null
    if (requiresRecurringToken.value) {
        recurring = checkoutRecurringInput(checkoutValues.recurring)
    }

    // Payment Items
    const payment = checkoutPaymentInput(checkoutValues.payment)

    const mutationData: CommitCheckoutInput = {
        checkoutId: checkoutId.value,
        customer: buildCheckoutCustomerInput(checkoutValues),
        recurring,
        payment,
        recaptchaToken: checkoutValues.recaptchaToken
    }

    if (preflightPhoneVerificationInput.value !== undefined) {
        mutationData.phoneVerification = preflightPhoneVerificationInput.value
    }

    submitCommitCheckoutMutation(mutationData)
})
</script>
